import { FC } from "react";

import { GlobalStyles, Stack } from "@mui/material";
import { Theme, ThemeProvider } from "@mui/material/styles";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { AssistantPaymentCard } from "@/features/DonorAssistant/PaymentCards/helpers/parsePaymentCards";
import { DonorPaymentDetailContent } from "@/features/DonorDashboard/DonorPaymentDetailDrawer/DonorPaymentDetailContent/DonorPaymentDetailContent";
import { DonorPaymentControl } from "@/features/DonorDashboard/DonorPaymentDetailDrawer/types";
import { ArrowLeft, Cross } from "@/icons/outlined";

interface PaymentDetailsProps {
  payment: AssistantPaymentCard;
  initialControl?: DonorPaymentControl | null;
  onBack: () => void;
  onClose: () => void;
  onAskAbout: () => void;
}

const liftOverlaysAbovePanel = (theme: Theme): Theme => ({
  ...theme,
  zIndex: { ...theme.zIndex, modal: theme.zIndex.hubspot, tooltip: theme.zIndex.hubspot },
});

const liftSnackbarsAbovePanel = (theme: Theme) => ({
  "body .notistack-SnackbarContainer": { zIndex: `${theme.zIndex.hubspot} !important` },
});

export const PaymentDetails: FC<PaymentDetailsProps> = ({ payment, initialControl, onBack, onClose, onAskAbout }) => {
  const { t } = useTranslate();
  return (
    <ThemeProvider theme={liftOverlaysAbovePanel}>
      <GlobalStyles styles={liftSnackbarsAbovePanel} />
      <Stack
        data-test="donor-assistant-payment-details"
        sx={(theme) => ({ height: "100%", minHeight: 0, bgcolor: theme.palette.surface.neutral.white })}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={(theme) => ({
            px: 1.5,
            py: 1.5,
            bgcolor: theme.palette.surface.brand.intense,
            color: theme.palette.text.reverse.intense,
          })}
        >
          <DashboardIconButton
            autoFocus
            size="small"
            variant="text"
            aria-label={t("dashboard", "donorAssistant.paymentDetails.back")}
            sx={(theme) => ({ color: theme.palette.text.reverse.intense })}
            onClick={onBack}
          >
            <ArrowLeft />
          </DashboardIconButton>
          <DashboardTypography
            component="h2"
            variant="subtitle2"
            vibe="text-reverse-intense"
            noWrap
            sx={{ flex: 1, minWidth: 0 }}
          >
            {t("dashboard", "donorAssistant.paymentDetails.title", { nonprofit: payment.organizationName })}
          </DashboardTypography>
          <DashboardIconButton
            size="small"
            variant="text"
            aria-label={t("dashboard", "assistant.widget.close")}
            sx={(theme) => ({ color: theme.palette.text.reverse.intense })}
            onClick={onClose}
          >
            <Cross />
          </DashboardIconButton>
        </Stack>
        <Stack sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, pt: 3 }}>
          <DonorPaymentDetailContent
            isOpen
            viewedFrom="DonorAssistant"
            initialControl={initialControl ?? undefined}
            content={{
              id: payment.id,
              formType: payment.formType,
              formId: payment.formId,
              commandId: payment.commandId,
              subscriptionId: payment.subscriptionId,
              organizationId: payment.organizationId,
            }}
          />
        </Stack>
        <Stack sx={(theme) => ({ p: 2, borderTop: `1px solid ${theme.palette.border.neutral.quiet}` })}>
          <DashboardButton vibe="brand" variant="filled" fullWidth onClick={onAskAbout}>
            {t("dashboard", "donorAssistant.paymentDetails.askAbout")}
          </DashboardButton>
        </Stack>
      </Stack>
    </ThemeProvider>
  );
};
